import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { useValidateDocumentMutation } from 'api/main';
import { APPROVABLE_DOCUMENTS, docTypes, validationStatus } from 'utils/constants';
import { parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { CustomButton, ValidationText } from 'components';
import { Box, Typography } from '@mui/material';

const buttonSx = { padding: '2px 5px', width: 'fit-content', height: 'fit-content' };

const ValidationActions = ({ doc, displayValidation, canValidate, setShowCommentLine }) => {
    const [ongoingValidationStatus, setOngoingValidationStatus] = useState(
        validationStatus.NOT_VALIDATED
    );

    const [validateDocument, { isLoading: validationLoader, isSuccess: validationSuccess }] =
        useValidateDocumentMutation();

    const handleAcceptDocument = useCallback(() => {
        setOngoingValidationStatus(validationStatus.ACCEPTED);
        const payload = {
            validationStatus: validationStatus.ACCEPTED,
            validationComment: '',
        };
        validateDocument({ id: doc.id, ...payload });
    }, [validateDocument, doc.id]);

    useEffect(() => {
        if (validationSuccess) {
            setShowCommentLine(false);
        }
    }, [validationSuccess, setShowCommentLine]);

    if (!displayValidation) return null;

    if (doc.validationStatus === validationStatus.ACCEPTED) {
        return <ValidationText validatedBy={doc.validatedBy} validatedAt={doc.validatedAt} />;
    }

    if (doc.validationStatus === validationStatus.REJECTED) {
        return (
            <Typography sx={{ fontSize: 15, fontStyle: 'italic', lineHeight: 1.6 }}>
                {`${doc.validationComment} | ${parseFullName(doc.validatedBy)} | ${moment(
                    doc.validatedAt
                ).format('DD.MM.YYYY')}`}
            </Typography>
        );
    }

    if (
        APPROVABLE_DOCUMENTS.includes(doc.type) &&
        canValidate &&
        (doc.type === docTypes.OTHER ? doc.createdBy?.id === doc.belongsTo?.id : true)
    ) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, paddingY: 1 }}>
                <CustomButton
                    noMargin
                    id={`accept-document-${doc.id}`}
                    label={texts.COMPLIANT}
                    sx={buttonSx}
                    onClick={handleAcceptDocument}
                    disabled={validationLoader}
                    size="medium"
                    loading={
                        validationLoader && ongoingValidationStatus === validationStatus.ACCEPTED
                    }
                />
                <CustomButton
                    noMargin
                    id={`reject-document-${doc.id}`}
                    label={texts.NON_COMPLIANT}
                    sx={buttonSx}
                    onClick={() => setShowCommentLine(true)}
                    disabled={validationLoader}
                />
            </Box>
        );
    }

    return null;
};

ValidationActions.propTypes = {
    doc: PropTypes.object.isRequired,
    displayValidation: PropTypes.bool,
    canValidate: PropTypes.bool,
    setShowCommentLine: PropTypes.func.isRequired,
};

export default ValidationActions;
